<template>
  <div class="fts-assign fts-row gutters">
    <!-- assignee -->
    <div
      class="assign__assignee fts-col"
      :class="{' fts-col-md-6': !view}"
    >
      <div class="assign-label">assign to</div>
      <!-- assignees:view -->
      <div class="assign-assignee" v-if="view">
        <div class="assign-assignee-wrapper" v-for="(x, ind) in assignData.assignees" :key="`${x}-${ind}`">
          <fts-profile :userName="x" />
        </div>
      </div>
      <!-- assignees:select -->
      <fts-select  
        :form-data="assignData"
        :item="{ fieldName: 'assignees' }"
        :options="users"
        label="displayName"
        :reduce="x => x && (x.userName || x.username)"
        multiple
        v-else
      >
        <div slot="selected-option-container" slot-scope="{ option }">
          <fts-profile :profile="option" />
        </div>
        <fts-profile slot="option" slot-scope="{option}" :profile="option" :profilePhotoSize="28">
          <template #profileName="{ userProfile, userProfileName, userProfileUsername }">
            <div class="assignee-option-name">{{userProfileName}}</div>
            <div class="assignee-option-username" v-if="userProfile.displayName">{{userProfileUsername}}</div>
          </template>
        </fts-profile>
      </fts-select>
      <!-- due -->
      <div class="assign-selected-date">
        <span>{{assignData.due ? 'due on' : 'no due'}}</span>
        <template v-if="assignData.due">
          <span
            class="assign-due"
            :class="{'assign-due--overdue': overdue}"
          >
            {{moment(assignData.due).format('ll')}}
          </span>
          <font-awesome-icon
            class="assign-due-action"
            :icon="['fas', 'times']"
            fixed-width
            @click="setDueDate(null)"
            v-if="!view"
          />
        </template>
      </div>
    </div>
    <!-- date -->
    <div class="assign__date fts-col-md-6" v-if="!view">
      <div class="assign-date-shortcut">
        <div
          class="assign-date-shortcut-option"
          :class="{ 'shortcut-option--active': selectedShortcutOption(x.endDate) }"
          v-for="x in dateShortcuts"
          :key="x.id"
          @click="setDueDate(x.endDate)"
        >
          {{x.label}}
        </div>
      </div>
      <VueCtkDateTimePicker
        :min-date="moment().startOf('day').format()"
        no-header
        no-button
        inline
        only-date
        format="llll"
        color="var(--fts-primary)"
        v-model="dueDate"
        class="v-ctk-date-time-picker"
        ref="ctkDateTimePicker"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import commons from '../commons/commons';
import { classificationStore } from '../store/classificationStore';

import VueCtkDateTimePicker from "vue-ctk-date-time-picker";
import "vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css";

import FTSSelect from "./FTSSelect";
import FTSProfile from "./FTSProfile";

export default {
  props: {
    assignData: {
      type: Object,
    },
    view: Boolean,
  },
  components :{
    "fts-select": FTSSelect,
    "fts-profile": FTSProfile,
    VueCtkDateTimePicker,
  },
  data: () => ({
    dateShortcuts: commons.taskDueDateShortcuts,
  }),
  methods: {
    moment,
    setDueDate(val) {
      this.dueDate = val;
    },
    selectedShortcutOption(val) {
      return this.assignData && moment(this.assignData.due).format('l') === moment(val).format('l');
    },
  },
  computed: {
    users() {
      return classificationStore.state.users || []
    },
    overdue() {
      if (this.assignData && this.assignData.due) {
        return moment(this.assignData.due).isBefore(moment().startOf('day'));
      }
    },
    dueDate: {
      get() {
        return this.assignData && this.assignData.due ? moment(this.assignData.due).endOf('day') : null;
      },
      set(val) {
        if (val)
          this.$set(this.assignData, 'due', moment(val).endOf('day'));
        else if (this.assignData.due)
          this.$delete(this.assignData, 'due');
      }
    }
  },
  mounted() {
    try {
      this.$nextTick(() => {
        let el = this.$refs.ctkDateTimePicker.$children[0].$children[0];
        window.removeEventListener("keydown", el.keyPressed);
      });
    } catch(err) {
      console.warn("Assign:RemoveEventListener", err);
    }
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-assign {
  .assign__assignee {
    margin-bottom: 2rem;
    .assign-assignee {
      @include flex-gap($gap: .375rem);
      .assign-assignee-wrapper {
        min-width: 0;
        padding: .375rem .75rem;
        border-radius: .25rem;
        background: var(--fts-gray-2);
      }
    }
    .assign-selected-date {
      margin-top: .75rem;
      font-size: .875rem;
      color: var(--fts-form-label);
    }
    .assign-due {
      font-weight: 700;
      color: var(--fts-text);
      &.assign-due--overdue {
        color: var(--fts-danger);
      }
    }
    .assign-due-action {
      font-size: .75rem;
      color: var(--fts-gray-5);
      cursor: pointer;
      &:hover {
        color: var(--fts-danger);
      }
    }
  }
  .assign__date {
    flex: 1;
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
    .assign-date-shortcut {
      display: flex;
      flex-direction: column;
      margin-top: 0.75rem;
      margin-right: 0.375rem;
      .assign-date-shortcut-option {
        @include text-overflow-ellipsis;
        @include user-select-none;
        margin: 0.375rem 0;
        padding: 0.375rem 0.75rem;
        border-radius: 0.25rem;
        color: var(--fts-gray-5);
        text-align: center;
        font-size: 0.75rem;
        line-height: 1.5;
        cursor: pointer;
        &:hover {
          @include theme() {
            background: rgba(theme-get('fts-gray-5'), 0.095);
          }
        }
        &.shortcut-option--active {
          background: rgba($color: $primaryColor, $alpha: 0.095);
          color: $primaryColor;
        }
      }
    }
    .v-ctk-date-time-picker {
      min-height: 350px;
    }
  }
  .assign-label {
    margin-bottom: .75rem;
    font-size: .875rem;
    color: var(--fts-form-label);
  }
}

.vs__dropdown-menu {
  .vs__dropdown-option {
    .assignee-option-username {
      color: var(--fts-gray-5);
    }
  }
  .assignee-option-name {
    font-weight: 500;
  }
  .assignee-option-username {
    font-weight: 300;
    font-size: 90%;  
  }
}
</style>